/**
 * The map itself: one WebGL pass over baked bytes.
 *
 * A slider drag changes four floats and redraws. Nothing is fetched, nothing is
 * re-uploaded and nothing is recomputed on the CPU, because stages 1 and 2 of
 * the mixer were baked by `propmap prod` and stages 3 and 4 are uniforms --
 * `mixer.ts` holds the arithmetic and the GLSL side by side.
 *
 * **Layers on one grid share one texture.** Price, crime and transport are all
 * on the 100 m grid, so their bytes are packed into the channels of a single
 * RGBA8 texture and the sixteen taps of the kernel are paid once for all three
 * rather than once each; price's footprint is on the same grid and rides in the
 * fourth channel. The schools layer is on the 250 m grid and gets its own. A
 * pack none of whose layers carries weight is not sampled at all, so a single
 * category's view costs sixteen taps and the blend thirty-two, against sixteen
 * per category and footprint if each were sampled separately -- which is the
 * difference between drawing and not drawing on a phone at three device pixels
 * to the CSS pixel.
 *
 * **Bytes rather than floats** is the other half of that. `R8`/`RGBA8` is
 * filterable and cheap on every mobile GPU, where `R32F` is neither -- and a
 * byte with 0 reserved for "no answer" carries its own validity mask, so there
 * is no second texture to sample and no second array to keep in step.
 *
 * The OSGB coordinates arrive per vertex from the emitted lattice and are
 * interpolated across each fragment by the rasteriser, because OSTN15 is a grid
 * shift and cannot be a formula in GLSL -- `project.ts` is the CPU half of the
 * same trick.
 */

import { MercatorCoordinate } from "maplibre-gl";
import type {
  CustomLayerInterface, CustomRenderMethodInput, Map as MapLibreMap,
} from "maplibre-gl";

import type { BlendSpec, GridSpec, LatticeSpec, LayerSpec } from "./manifest.ts";
import { ACTIVATIONS, BLENDS, MIXER_GLSL, SAMPLING_GLSL } from "./mixer.ts";
import { hexToRgb } from "./ramp.ts";

/** How strongly the raster is drawn over the basemap. */
export const OPACITY = 0.65;

/**
 * Where the raster starts standing back, and how far.
 *
 * A layer has no detail below its own cell, and at street zoom a 100 m cell is
 * most of the screen: drawn at full strength it is a flat wash over the street
 * map, claiming a precision it does not have and hiding the context a reader
 * has zoomed in for. So past `FADE_FROM` it fades toward `FADE_TO` of its
 * strength, and the evidence points -- which do have street-level precision --
 * carry the view instead.
 */
const FADE_FROM = 13.5;
const FADE_UNTIL = 16.5;
const FADE_TO = 0.3;

/**
 * "We do not know" must not be a step on the ramp: the pale end of a sequential
 * ramp is within a percent of the page colour, and a reader cannot tell "worst
 * in London" from "no data" if they are the same pixel.
 */
const NO_DATA_COLOUR = "#cfc9bd";

/** Below this, the share of a point with no answer is rounding rather than grey. */
const GREY_DUST = 1e-9;

export interface LoadedLayer {
  spec: LayerSpec;
  /** the baked percentile, one byte a cell, 0 where the layer has no answer */
  bytes: Uint8Array;
  /** how far each cell is inside this layer's footprint, or null without one */
  presence: Uint8Array | null;
}

/** Which texture and channel a layer's bytes, or its footprint, live in. */
interface Slot {
  pack: number;
  channel: number;
}

/**
 * One RGBA8 texture holding up to four channels that share a grid: layer
 * scores, where 0 is "no answer", and footprints, which hold everywhere.
 */
interface Pack {
  grid: GridSpec;
  /** the layers whose score or footprint is in this pack */
  layers: number[];
  bytes: Uint8Array;
  texture: WebGLTexture | null;
}

const CHANNELS = 4;

function gridKey(grid: GridSpec): string {
  return `${grid.x0}:${grid.y0}:${grid.step}:${grid.nx}:${grid.ny}`;
}

interface Packed {
  packs: Pack[];
  /** per layer, where its score is */
  slots: Slot[];
  /** per layer, where its footprint is, or null without one */
  presence: (Slot | null)[];
}

/** Group the scores and footprints onto as few textures as their grids allow. */
function packLayers(layers: LoadedLayer[]): Packed {
  type Channel = { layer: number; presence: boolean };
  const packs: Pack[] = [];
  const slots: Slot[] = new Array(layers.length);
  const presence: (Slot | null)[] = layers.map(() => null);
  const byGrid = new Map<string, Channel[]>();
  const add = (layer: number, isPresence: boolean): void => {
    const key = gridKey(layers[layer]!.spec.grid);
    byGrid.set(key, [...(byGrid.get(key) ?? []), { layer, presence: isPresence }]);
  };
  layers.forEach((_, i) => add(i, false));
  layers.forEach((layer, i) => {
    if (layer.presence) add(i, true);
  });
  for (const channels of byGrid.values()) {
    for (let start = 0; start < channels.length; start += CHANNELS) {
      const group = channels.slice(start, start + CHANNELS);
      const grid = layers[group[0]!.layer]!.spec.grid;
      const pack: Pack = {
        grid,
        layers: [...new Set(group.map((c) => c.layer))],
        bytes: new Uint8Array(grid.nx * grid.ny * CHANNELS),
        texture: null,
      };
      group.forEach((c, channel) => {
        const slot = { pack: packs.length, channel };
        if (c.presence) {
          presence[c.layer] = slot;
          fill(pack, channel, layers[c.layer]!.presence!);
        } else {
          slots[c.layer] = slot;
          fill(pack, channel, layers[c.layer]!.bytes);
        }
      });
      packs.push(pack);
    }
  }
  return { packs, slots, presence };
}

/** Write one layer's bytes into its channel of its pack. */
function fill(pack: Pack, channel: number, bytes: Uint8Array): void {
  const cells = pack.grid.nx * pack.grid.ny;
  for (let c = 0; c < cells; c++) pack.bytes[c * CHANNELS + channel] = bytes[c]!;
}

const VERTEX = `#version 300 es
precision highp float;
uniform mat4 u_matrix;
in vec2 a_mercator;
in vec2 a_osgb;
out vec2 v_osgb;
void main() {
  v_osgb = a_osgb;
  gl_Position = u_matrix * vec4(a_mercator, 0.0, 1.0);
}`;

function fragmentSource(layers: LoadedLayer[], packs: Pack[], packed: Packed): string {
  const n = layers.length;
  const { slots, presence } = packed;

  // `plain` is the kernel-weighted mean with no "no answer" mask, which is how
  // a footprint channel is read; a score channel reads `value`.
  const packDecls = packs.map((_, p) => `
uniform sampler2D u_pack${p};
uniform vec3 u_grid${p};   // x0, y0, step
uniform vec2 u_size${p};   // nx, ny
uniform bool u_use${p};    // whether any layer in this pack carries weight

void samplePack${p}(vec2 point, out vec4 value, out vec4 coverage, out vec4 plain) {
  vec2 u = (point - u_grid${p}.xy) / u_grid${p}.z;
  vec2 base = floor(u);
  vec4 wx = kernel(u.x - base.x);
  vec4 wy = kernel(u.y - base.y);
  value = vec4(0.0);
  coverage = vec4(0.0);
  plain = vec4(0.0);
  float weight = 0.0;
  for (int j = 0; j < 4; j++) {
    if (wy[j] == 0.0) continue;
    int cy = int(base.y) + j - 1;
    if (cy < 0 || float(cy) >= u_size${p}.y) continue;
    for (int i = 0; i < 4; i++) {
      float w = wx[i] * wy[j];
      if (w == 0.0) continue;
      int cx = int(base.x) + i - 1;
      if (cx < 0 || float(cx) >= u_size${p}.x) continue;
      // the texel is the byte over 255; a byte of 0 is "no answer" and takes
      // no part, which is what makes the mask unnecessary
      vec4 texel = texelFetch(u_pack${p}, ivec2(cx, cy), 0);
      vec4 has = step(0.5 / 255.0, texel);
      coverage += w * has;
      value += w * has * ((texel * 255.0 - 1.0) / ${LEVELS_GLSL});
      plain += w * texel;
      weight += w;
    }
  }
  value /= max(coverage, vec4(1e-9));
  plain = weight > 0.0 ? plain / weight : vec4(1.0);
}`).join("");

  const sampleCalls = packs.map((_, p) => `
  vec4 value${p} = vec4(0.0), coverage${p} = vec4(0.0), plain${p} = vec4(1.0);
  if (u_use${p}) samplePack${p}(p, value${p}, coverage${p}, plain${p});`).join("");

  const accumulate = layers.map((_, i) => {
    const slot = slots[i]!;
    const channel = "xyzw"[slot.channel];
    const footprint = presence[i];
    return `
  {
    float s = value${slot.pack}.${channel};
    float coverage = coverage${slot.pack}.${channel};
    onGrid = onGrid || onPack${slot.pack};
    if (u_weight[${i}] > 0.0) {${footprint ? `
      // outside this layer's footprint it does not apply, so nothing is drawn
      home *= plain${footprint.pack}.${"xyzw"[footprint.channel]};` : ""}
      float k = edgeStep(coverage);
      noneKnown *= 1.0 - k;
      if (coverage <= 0.0) {
        allPass = 0.0;
      } else {
        acc = blendAdd(acc, activate(s, u_activation[${i}], u_gain[${i}]), u_weight[${i}]);
        allPass *= k;
        total += u_weight[${i}];
      }
    }
  }`;
  }).join("");

  const onGridTests = packs.map((_, p) => `
  vec2 u${p} = (p - u_grid${p}.xy) / u_grid${p}.z;
  bool onPack${p} = u${p}.x >= -0.5 && u${p}.y >= -0.5
    && u${p}.x <= u_size${p}.x - 0.5 && u${p}.y <= u_size${p}.y - 0.5;`).join("");

  return `#version 300 es
precision highp float;

uniform float u_weight[${n}];
uniform float u_gain[${n}];
uniform int   u_activation[${n}];
uniform float u_cutoff;
uniform float u_gamma;
uniform float u_rampFloor;
uniform float u_opacity;
uniform vec3  u_noData;
uniform sampler2D u_ramp;
${SAMPLING_GLSL}
${MIXER_GLSL}
${packDecls}

in vec2 v_osgb;
out vec4 fragColor;

void main() {
  vec2 p = v_osgb;
  float acc = 0.0, total = 0.0;
  // noneKnown: no weighted layer has an answer here; allPass: every one does.
  // Each is 0 or 1 cell by cell and fractional across a soft edge.
  float noneKnown = 1.0, allPass = 1.0, home = 1.0;
  bool onGrid = false;
${onGridTests}
${sampleCalls}
${accumulate}

  if (!onGrid || total == 0.0) discard;
  float score = blendFinish(acc, total);
  // Under the cutoff nothing is drawn, and just above it a place fades in.
  float colourA = allPass * fadeStep(score - u_cutoff) * home;
  float unknown = 1.0 - noneKnown - allPass;
  float greyA = (unknown > ${GREY_DUST.toExponential()} ? unknown : 0.0) * home;
  if (colourA + greyA <= 0.0) discard;
  float t = u_rampFloor + (1.0 - u_rampFloor) * pow(clamp(score, 0.0, 1.0), u_gamma);
  vec3 premultiplied = texture(u_ramp, vec2(t, 0.5)).rgb * colourA + u_noData * greyA;
  fragColor = vec4(premultiplied * u_opacity, (colourA + greyA) * u_opacity);
}`;
}

/** `propmap.prod.LEVELS`, spelled for GLSL. */
const LEVELS_GLSL = "254.0";

function compile(gl: WebGL2RenderingContext, vertex: string, fragment: string): WebGLProgram {
  const build = (kind: number, source: string): WebGLShader => {
    const shader = gl.createShader(kind)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      throw new Error(`propmap: shader failed: ${gl.getShaderInfoLog(shader)}`);
    }
    return shader;
  };
  const program = gl.createProgram()!;
  gl.attachShader(program, build(gl.VERTEX_SHADER, vertex));
  gl.attachShader(program, build(gl.FRAGMENT_SHADER, fragment));
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(`propmap: link failed: ${gl.getProgramInfoLog(program)}`);
  }
  return program;
}

interface Mesh {
  vertices: Float32Array;
  indices: Uint16Array;
  stride: number;
}

/**
 * The lattice as a triangle mesh: Mercator for MapLibre's matrix, OSGB as an
 * attribute the rasteriser interpolates for us.
 */
function buildMesh(spec: LatticeSpec, osgb: Float32Array): Mesh {
  const { n_lon: nx, n_lat: ny, lon0, lon1, lat0, lat1 } = spec;
  const count = nx * ny;
  if (count > 65535) {
    throw new Error(`propmap: lattice has ${count} vertices; Uint16 indices hold 65535`);
  }
  const vertices = new Float32Array(count * 4);
  for (let j = 0; j < ny; j++) {
    const lat = lat0 + ((lat1 - lat0) * j) / (ny - 1);
    for (let i = 0; i < nx; i++) {
      const lon = lon0 + ((lon1 - lon0) * i) / (nx - 1);
      const m = MercatorCoordinate.fromLngLat({ lng: lon, lat });
      const v = (j * nx + i) * 4;
      const o = (j * nx + i) * 2;
      vertices[v] = m.x;
      vertices[v + 1] = m.y;
      vertices[v + 2] = osgb[o]!;
      vertices[v + 3] = osgb[o + 1]!;
    }
  }
  const indices = new Uint16Array((nx - 1) * (ny - 1) * 6);
  let k = 0;
  for (let j = 0; j < ny - 1; j++) {
    for (let i = 0; i < nx - 1; i++) {
      const a = j * nx + i;
      const b = a + 1;
      const c = a + nx;
      const d = c + 1;
      indices[k++] = a; indices[k++] = c; indices[k++] = b;
      indices[k++] = b; indices[k++] = c; indices[k++] = d;
    }
  }
  return { vertices, indices, stride: 16 };
}

export class BlendLayer implements CustomLayerInterface {
  readonly id = "propmap-blend";
  readonly type = "custom" as const;
  readonly renderingMode = "2d" as const;

  private readonly layers: LoadedLayer[];
  private readonly spec: BlendSpec;
  private readonly mesh: Mesh;
  private readonly packs: Pack[];
  private readonly packed: Packed;

  private map: MapLibreMap | null = null;
  private program: WebGLProgram | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private buffers: WebGLBuffer[] = [];
  private rampTexture: WebGLTexture | null = null;
  private uniforms: Record<string, WebGLUniformLocation | null> = {};

  /** Per layer, the slider as the reader has it. */
  private weights: number[];
  private ramp: Uint8Array;
  private pendingRamp = true;
  private pendingPacks = new Set<number>();
  private opacity = OPACITY;

  constructor(layers: LoadedLayer[], spec: BlendSpec, lattice: LatticeSpec,
              osgb: Float32Array, ramp: Uint8Array) {
    this.layers = layers;
    this.spec = spec;
    this.mesh = buildMesh(lattice, osgb);
    this.ramp = ramp;
    this.weights = layers.map(() => 0);
    this.packed = packLayers(layers);
    this.packs = this.packed.packs;
  }

  /** The sliders, in manifest order. A category view is every other weight at zero. */
  setWeights(weights: number[]): void {
    this.weights = weights;
    this.map?.triggerRepaint();
  }

  /** The ramp the blend or the selected category is drawn through. */
  setRamp(ramp: Uint8Array): void {
    this.ramp = ramp;
    this.pendingRamp = true;
    this.map?.triggerRepaint();
  }

  setOpacity(opacity: number): void {
    this.opacity = opacity;
    this.map?.triggerRepaint();
  }

  /**
   * Swap one layer's bytes -- the schools phase, which is three baked rasters
   * the client already holds. A repack and one upload, so changing phase costs
   * no fetch.
   */
  setBytes(index: number, bytes: Uint8Array): void {
    const slot = this.packed.slots[index];
    const layer = this.layers[index];
    if (!slot || !layer) return;
    layer.bytes = bytes;
    fill(this.packs[slot.pack]!, slot.channel, bytes);
    this.pendingPacks.add(slot.pack);
    this.map?.triggerRepaint();
  }

  onAdd(map: MapLibreMap, gl: WebGL2RenderingContext): void {
    this.map = map;
    this.program = compile(gl, VERTEX, fragmentSource(this.layers, this.packs, this.packed));

    this.vao = gl.createVertexArray();
    gl.bindVertexArray(this.vao);
    const vbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.mesh.vertices, gl.STATIC_DRAW);
    const aMercator = gl.getAttribLocation(this.program, "a_mercator");
    const aOsgb = gl.getAttribLocation(this.program, "a_osgb");
    gl.enableVertexAttribArray(aMercator);
    gl.vertexAttribPointer(aMercator, 2, gl.FLOAT, false, this.mesh.stride, 0);
    gl.enableVertexAttribArray(aOsgb);
    gl.vertexAttribPointer(aOsgb, 2, gl.FLOAT, false, this.mesh.stride, 8);
    const ibo = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.mesh.indices, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    this.buffers = [vbo, ibo];

    for (const pack of this.packs) {
      pack.texture = this.texture(gl, gl.RGBA8, gl.RGBA, pack.grid.nx, pack.grid.ny, pack.bytes);
    }
    this.rampTexture = this.texture(gl, gl.RGBA8, gl.RGBA, 256, 1, this.ramp, true);

    const names = ["u_matrix", "u_weight", "u_gain", "u_activation", "u_cutoff", "u_gamma",
      "u_rampFloor", "u_opacity", "u_noData", "u_ramp", "u_blend", "u_p", "u_blendFloor",
      "u_sampling", "u_edgeSoft", "u_edgeFade"];
    this.packs.forEach((_, p) => names.push(`u_pack${p}`, `u_grid${p}`, `u_size${p}`, `u_use${p}`));
    for (const name of names) {
      this.uniforms[name] = gl.getUniformLocation(this.program, name);
    }
  }

  private texture(gl: WebGL2RenderingContext, internalFormat: number, format: number,
                  width: number, height: number, data: Uint8Array,
                  linear = false): WebGLTexture {
    const texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    // One byte a pixel, not four. WebGL pads every texture row up to
    // `UNPACK_ALIGNMENT`, which defaults to 4, so a single-channel texture
    // whose width is not a multiple of four is read as wider than it is: the
    // 583-wide footprint wants 584 * 456 bytes and the asset has 583 * 456.
    // The upload then fails with INVALID_OPERATION, the sampler reads zero,
    // and -- because a footprint multiplies everything drawn -- the map comes
    // out blank wherever that layer carries weight. Nothing throws.
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, width, height, 0, format,
                  gl.UNSIGNED_BYTE, data);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    const filter = linear ? gl.LINEAR : gl.NEAREST;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return texture;
  }

  onRemove(_map: MapLibreMap, gl: WebGL2RenderingContext): void {
    for (const pack of this.packs) if (pack.texture) gl.deleteTexture(pack.texture);
    if (this.rampTexture) gl.deleteTexture(this.rampTexture);
    for (const buffer of this.buffers) gl.deleteBuffer(buffer);
    if (this.vao) gl.deleteVertexArray(this.vao);
    if (this.program) gl.deleteProgram(this.program);
    this.map = null;
  }

  render(gl: WebGL2RenderingContext, options: CustomRenderMethodInput): void {
    if (!this.program) return;
    const spec = this.spec;

    if (this.pendingRamp && this.rampTexture) {
      gl.bindTexture(gl.TEXTURE_2D, this.rampTexture);
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 256, 1, gl.RGBA, gl.UNSIGNED_BYTE, this.ramp);
      this.pendingRamp = false;
    }
    for (const p of this.pendingPacks) {
      const pack = this.packs[p];
      if (!pack?.texture) continue;
      gl.bindTexture(gl.TEXTURE_2D, pack.texture);
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, pack.grid.nx, pack.grid.ny, gl.RGBA,
                       gl.UNSIGNED_BYTE, pack.bytes);
    }
    this.pendingPacks.clear();

    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.uniformMatrix4fv(this.uniforms["u_matrix"]!, false,
      options.defaultProjectionData.mainMatrix as Float32Array);

    // The weight the blend reads is the slider over its maximum, which is what
    // `propmap.mixer.blend` uses; every blend that divides by the total is
    // scale-free, so this is the same picture the reference draws.
    gl.uniform1fv(this.uniforms["u_weight"]!,
      new Float32Array(this.weights.map((w) => Math.max(w, 0) / spec.weight_max)));
    gl.uniform1fv(this.uniforms["u_gain"]!, new Float32Array(this.layers.map((layer, i) => {
      const u = Math.min(Math.max(Math.max(this.weights[i] ?? 0, 0) / spec.weight_max, 0), 1);
      return layer.spec.k * u;
    })));
    gl.uniform1iv(this.uniforms["u_activation"]!, new Int32Array(this.layers.map((layer) =>
      Math.max(ACTIVATIONS.indexOf(layer.spec.activation), 0))));

    gl.uniform1i(this.uniforms["u_blend"]!, Math.max(BLENDS.indexOf(spec.blend), 0));
    gl.uniform1f(this.uniforms["u_p"]!, spec.power_p);
    gl.uniform1f(this.uniforms["u_blendFloor"]!, spec.blend_floor);
    gl.uniform1f(this.uniforms["u_cutoff"]!, spec.cutoff);
    gl.uniform1f(this.uniforms["u_gamma"]!, spec.display_gamma);
    gl.uniform1f(this.uniforms["u_rampFloor"]!, spec.ramp_floor);
    gl.uniform1f(this.uniforms["u_sampling"]!, spec.sampling);
    gl.uniform1f(this.uniforms["u_edgeSoft"]!, spec.edge_soft);
    gl.uniform1f(this.uniforms["u_edgeFade"]!, spec.edge_fade);
    const sum = this.weights.reduce((a, b) => a + Math.max(b, 0), 0);
    const zoom = this.map?.getZoom() ?? FADE_FROM;
    const t = Math.min(Math.max((zoom - FADE_FROM) / (FADE_UNTIL - FADE_FROM), 0), 1);
    const fade = 1 - t * (1 - FADE_TO);
    gl.uniform1f(this.uniforms["u_opacity"]!, sum > 0 ? this.opacity * fade : 0);
    const grey = hexToRgb(NO_DATA_COLOUR);
    gl.uniform3f(this.uniforms["u_noData"]!, grey.r / 255, grey.g / 255, grey.b / 255);

    let unit = 0;
    const bind = (slot: string, texture: WebGLTexture | null): void => {
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(this.uniforms[slot]!, unit);
      unit++;
    };
    this.packs.forEach((pack, p) => {
      bind(`u_pack${p}`, pack.texture);
      gl.uniform3f(this.uniforms[`u_grid${p}`]!, pack.grid.x0, pack.grid.y0, pack.grid.step);
      gl.uniform2f(this.uniforms[`u_size${p}`]!, pack.grid.nx, pack.grid.ny);
      gl.uniform1i(this.uniforms[`u_use${p}`]!,
                   pack.layers.some((i) => (this.weights[i] ?? 0) > 0) ? 1 : 0);
    });
    bind("u_ramp", this.rampTexture);

    gl.drawElements(gl.TRIANGLES, this.mesh.indices.length, gl.UNSIGNED_SHORT, 0);
    gl.bindVertexArray(null);
  }
}
